import { api } from '../../../utils/helpers';

export function newDispatchRequestId() {
  return Array.from(crypto.getRandomValues(new Uint8Array(16)), byte => byte.toString(16).padStart(2, '0')).join('');
}

export function matchesDispatchOperation(result, expected) {
  const ids = result?.packageIds;
  return result?.dispatchRequestId === expected.dispatchRequestId && result.sourceSha === expected.sourceSha
    && result.targetStage === expected.targetStage && result.spaceId === expected.canonicalSpaceId
    && Array.isArray(ids) && ids.length === expected.packageIds.length && new Set(ids).size === ids.length
    && expected.packageIds.every(id => ids.includes(id))
    && Array.isArray(result.laeufe) && result.laeufe.length === ids.length
    && new Set(result.laeufe.map(item => item?.packageId)).size === ids.length
    && result.laeufe.every(item => ids.includes(item?.packageId));
}

export function dispatchDefinitelyNotStarted(result, expected) {
  return matchesDispatchOperation(result, expected) && result.status === 'failed' && result.finished === true
    && !result.run && result.laeufe.every(item => item.status === 'not_started' && !item.run);
}

function delay(signal) {
  return new Promise(resolve => {
    const finish = () => { clearTimeout(timer); signal.removeEventListener('abort', finish); resolve(); };
    const timer = setTimeout(finish, 10000);
    signal.addEventListener('abort', finish, { once: true });
    if (signal.aborted) finish();
  });
}

// Reads are bound to the client-created request, never to a latest run or a SHA alone.
export async function recoverDispatch(expected, signal) {
  const deadline = Date.now() + 180000;
  for (let attempt = 0; attempt < 18 && !signal.aborted && Date.now() < deadline; attempt += 1) {
    try {
      const result = await api(`/api/cicd/transport/dispatch-operations/${expected.dispatchRequestId}?spaceId=${encodeURIComponent(expected.spaceId)}`,
        { signal, retry: false, cache: 'no-store', timeout: Math.min(10000, deadline - Date.now()) });
      if (signal.aborted) return null;
      if (!matchesDispatchOperation(result, expected)) throw Object.assign(new Error('DISPATCH_OPERATION_INVALID'), { code: 'DISPATCH_OPERATION_INVALID' });
      if (result.status === 'dispatched' && result.laeufe.every(item => item.status === 'dispatched' && item.run?.id)) return result;
      if (result.status === 'pending_approval' && result.finished === true && result.laeufe.every(item => item.status === 'not_started' && !item.run)) return result;
      if (dispatchDefinitelyNotStarted(result, expected)) return result;
      if (result.status === 'failed') return null;
      if (!['preparing', 'unknown'].includes(result.status)) return null;
    } catch (error) {
      if (signal.aborted) return null;
      if ([401, 403].includes(error?.status)) throw Object.assign(new Error('DISPATCH_OPERATION_ACCESS_REVOKED'), { code: 'DISPATCH_OPERATION_ACCESS_REVOKED' });
      if (error?.status !== 404 && !(error?.status >= 500) && !['AbortError', 'TypeError'].includes(error?.name)) throw error;
    }
    if (attempt < 17 && Date.now() + 10000 < deadline && !signal.aborted) await delay(signal);
  }
  return null;
}
